/* eslint-disable jsx-a11y/no-interactive-element-to-noninteractive-role */
import { ReactNode } from 'react';
import {
  Avatar,
  AvatarProps,
  Badge,
  Box,
  BoxProps,
  Divider,
  Icon,
  IconProps,
  Link,
  Skeleton,
  SkeletonBox,
  Text,
  TextProps,
  TypeIconName,
} from '@bitrise/bitkit';

import PlatformIcon from '@/components/icon/PlatformIcon/PlatformIcon';
import { PublicApiApp } from '@/models/App';

type AppAvatarProps = { app?: Pick<PublicApiApp, 'title' | 'avatarUrl'> } & Omit<
  AvatarProps,
  'iconName' | 'name' | 'url' | 'variant'
>;
const AppAvatar = ({ app, ...rest }: AppAvatarProps): JSX.Element => {
  return app ? (
    <Avatar name={app.title} src={app.avatarUrl} size="40" variant="project" {...rest} />
  ) : (
    <SkeletonBox height="40" width="40" borderRadius="8" />
  );
};

type AppPlatformIconProps = { app?: Pick<PublicApiApp, 'projectType'> } & Omit<IconProps, 'name' | 'type'>;
const AppPlatformIcon = ({ app, textColor = 'purple.10', ...rest }: AppPlatformIconProps): JSX.Element =>
  app ? (
    <PlatformIcon type={app.projectType} textColor={textColor} {...rest} />
  ) : (
    <SkeletonBox height="20px" width="20px" />
  );

type AppTitleProps = { app?: Pick<PublicApiApp, 'title'> } & TextProps;
const AppTitle = ({ app, textColor = 'purple.10', ...rest }: AppTitleProps): JSX.Element =>
  app ? (
    <Text fontWeight="bold" textColor={textColor} hasEllipsis {...rest}>
      {app.title}
    </Text>
  ) : (
    <SkeletonBox height="20px" width="128px" />
  );

type AppBadgeProps = { app?: Pick<PublicApiApp, 'status' | 'projectType' | 'isDisabled'> };
const AppBadge = ({ app }: AppBadgeProps): JSX.Element | null => {
  if (!app) {
    return null;
  }
  if (app.status === -1 || app.projectType === null) {
    return (
      <Badge variant="bold" colorScheme="warning">
        Incomplete
      </Badge>
    );
  }
  if (app.isDisabled) {
    return <Badge>Inactive</Badge>;
  }
  return null;
};

type ContainerElementProps = BoxProps;
const ContainerElement = ({ children, ...rest }: ContainerElementProps): JSX.Element => (
  <>
    <Box display="flex" alignItems="center" gap="8" paddingY="12" {...rest}>
      {children}
    </Box>
    <Divider />
  </>
);

type AppListSimpleItemProps = { icon: TypeIconName; text: ReactNode } & Omit<ContainerElementProps, 'hover'>;
const AppListSimpleItem = ({ icon, text, ...rest }: AppListSimpleItemProps): JSX.Element => (
  <ContainerElement {...rest}>
    <Icon name={icon} marginX="8" />
    <Text fontWeight="bold" paddingY="8" margin="0">
      {text}
    </Text>
  </ContainerElement>
);

const AppListEmptyItem = (): JSX.Element => (
  <AppListSimpleItem icon="DeleteNope" text="We cannot find any projects." textColor="purple.10" />
);

type AppListErrorItemProps = { message: ReactNode };
const AppListErrorItem = ({ message }: AppListErrorItemProps) => (
  <AppListSimpleItem icon="ErrorGeneral" text={message} textColor="red.40" />
);

type AppListItemContentProps = { app?: PublicApiApp };
const AppListItemContent = ({ app }: AppListItemContentProps): JSX.Element => {
  return (
    <ContainerElement>
      <AppAvatar app={app} />
      <Box display="flex" gap="4" alignItems="center" flexShrink={1} overflow="hidden">
        <AppPlatformIcon app={app} _groupHover={{ color: 'purple.50' }} />
        <AppTitle app={app} _groupHover={{ color: 'purple.50' }} />
        <AppBadge app={app} />
      </Box>
    </ContainerElement>
  );
};

const AppListSkeletonItem = (): JSX.Element => (
  <Skeleton isActive>
    <AppListItemContent />
  </Skeleton>
);

type AppListItemProps = { app: PublicApiApp };
const AppListItem = ({ app }: AppListItemProps): JSX.Element => (
  <Link display="block" borderRadius="8" href={`/app/${app.slug}`} role="group">
    <AppListItemContent app={app} />
  </Link>
);

export {
  AppListItemProps,
  AppListSkeletonItem,
  AppListEmptyItem,
  AppListErrorItem,
  AppAvatar,
  AppTitle,
  AppBadge,
  AppPlatformIcon,
};
export default AppListItem;
